import {
	Box,
	Flex,
	Heading,
	HStack,
	Icon,
	Text,
	VStack,
	useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaShieldAlt, FaChartLine, FaCheckCircle, FaArrowRight, FaWrench } from 'react-icons/fa';
import { useCreateOrganizationSettings, type SettingsPreset } from '../../hooks/useCreateOrganizationSettings';
import { StepFooter } from './StepFooter';

interface OrgSettingsPresetStepProps {
	organizationId: string;
	onComplete: () => void;
	onBack?: () => void;
}

const PRESETS: Array<{
	value: SettingsPreset;
	title: string;
	subtitle: string;
	icon: React.ElementType;
	color: string;
	bullets: string[];
}> = [
	{
		value: 'oem-only',
		title: 'OEM Only',
		subtitle: 'Prioritize original parts',
		icon: FaShieldAlt as React.ElementType,
		color: 'blue',
		bullets: [
			'Always prefer OEM parts',
			'Tighter delivery windows (48–96 hrs)',
			'Auto price-match enabled',
			'Re-key all estimate lines',
			'Dealer referral allowed',
			'No expedited quoting',
		],
	},
	{
		value: 'high-margin',
		title: 'High Margin',
		subtitle: 'Maximize profit per repair',
		icon: FaChartLine as React.ElementType,
		color: 'green',
		bullets: [
			'Best-price part selection (OEM + A/M)',
			'Wider delivery windows (120–240 hrs)',
			'Auto-requote stale orders',
			'Auto-accept supplier changes',
			'Flag negative-margin parts',
			'Re-key quote-only lines',
		],
	},
	{
		value: 'custom',
		title: 'Build from Scratch',
		subtitle: 'Configure everything yourself',
		icon: FaWrench as React.ElementType,
		color: 'gray',
		bullets: [
			'No pre-configured settings applied',
			'Full control over every option',
			'Set up at your own pace',
			'Customize from the Settings page',
		],
	},
];

export const OrgSettingsPresetStep = ({
	organizationId,
	onComplete,
	onBack,
}: OrgSettingsPresetStepProps) => {
	const toast = useToast();
	const createSettings = useCreateOrganizationSettings();
	const [selected, setSelected] = useState<SettingsPreset | null>(null);

	const PRESET_LABELS: Record<SettingsPreset, string> = {
		'oem-only': 'OEM Only',
		'high-margin': 'High Margin',
		'custom': 'Build from Scratch',
	};

	const handleConfirm = async () => {
		if (!selected) return;
		try {
			await createSettings.mutateAsync({ organizationId, preset: selected });
			toast({
				title: 'Settings applied',
				description: `${PRESET_LABELS[selected]} preset saved.`,
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			onComplete();
		} catch {
			toast({
				title: 'Failed to save settings',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		}
	};

	return (
		<Flex direction="column" flex={1} width="100%" minH={0}>
			<Box flex={1} minH={0} overflowY="auto" pt={4}>
				<Box mb={6} textAlign="center">
					<Heading size="md" mb={1}>Choose Your Preset</Heading>
					<Text color="gray.500" fontSize="sm">
						Select a configuration profile to get started. You can customize all settings later from the Settings page.
					</Text>
				</Box>

				<HStack spacing={4} align="stretch" justify="center" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
					{PRESETS.map((preset) => {
						const isSelected = selected === preset.value;
						return (
							<Box
								key={preset.value}
								as="button"
								type="button"
								onClick={() => setSelected(preset.value)}
								flex={{ base: '1 1 100%', md: '1 1 0%' }}
								maxW={{ md: '320px' }}
								borderWidth={2}
								borderColor={isSelected ? `${preset.color}.400` : 'gray.200'}
								borderRadius="lg"
								p={5}
								bg={isSelected ? `${preset.color}.50` : 'white'}
								transition="all 0.2s ease"
								_hover={{
									borderColor: `${preset.color}.300`,
									bg: isSelected ? `${preset.color}.50` : 'gray.50',
								}}
								textAlign="left"
								cursor="pointer"
								position="relative"
								display="flex"
								flexDirection="column"
								justifyContent="center"
							>
								<HStack spacing={3} align="center" justify="space-between" mb={3}>
									<HStack spacing={3} align="center">
										<Flex
											w="40px" h="40px"
											borderRadius="lg"
											bg={isSelected ? `${preset.color}.500` : 'gray.100'}
											align="center" justify="center"
											transition="all 0.2s ease"
										>
											<Icon
												as={preset.icon as React.ElementType}
												color={isSelected ? 'white' : 'gray.500'}
												boxSize={5}
											/>
										</Flex>
										<Box>
											<Text fontSize="md" fontWeight="bold" color="gray.800">{preset.title}</Text>
											<Text fontSize="xs" color="gray.500">{preset.subtitle}</Text>
										</Box>
									</HStack>
									{isSelected && <Icon as={FaArrowRight as React.ElementType} color={`${preset.color}.500`} boxSize={3.5} />}
								</HStack>

								<VStack align="stretch" spacing={1.5} pl={1}>
									{preset.bullets.map((bullet, i) => (
										<HStack key={i} spacing={2} align="flex-start">
											<Icon
												as={FaCheckCircle as React.ElementType}
												color={isSelected ? `${preset.color}.400` : 'gray.300'}
												boxSize={3}
												mt="3px"
												flexShrink={0}
											/>
											<Text fontSize="xs" color="gray.600" lineHeight="short">{bullet}</Text>
										</HStack>
									))}
								</VStack>
							</Box>
						);
					})}
				</HStack>
			</Box>

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				primary={{
					label: selected === 'custom' ? 'Continue' : 'Apply Preset',
					onClick: () => void handleConfirm(),
					disabled: !selected,
					isLoading: createSettings.isPending,
					loadingText: 'Saving...',
				}}
			/>
		</Flex>
	);
};
